import { useState } from "react";
import { useMutation } from "@apollo/client";
import { gql } from "../../../__generated__";
import type {
  AdminSyncTossPayoutStatusMutation,
  AdminSyncTossPayoutStatusMutationVariables,
} from "../../../__generated__/graphql";
import { notify, notifyError } from "../../../utils/toast";

const ADMIN_SYNC_TOSS_PAYOUT_STATUS = gql(`
  mutation AdminSyncTossPayoutStatus($tossPayoutId: String!) {
    adminSyncTossPayoutStatus(tossPayoutId: $tossPayoutId) {
      ok
      error
      status
    }
  }
`);

/**
 * 웹훅 누락 시 토스 GET 지급 단건으로 DB의 tossPayoutStatus·paidAt을 맞춥니다.
 * 토스 콘솔과 동일 건의 지급 ID(FPA_…)를 입력하세요.
 */
export default function AdminTossPayoutSyncPage() {
  const [tossPayoutId, setTossPayoutId] = useState("");

  const [sync, { loading }] = useMutation<
    AdminSyncTossPayoutStatusMutation,
    AdminSyncTossPayoutStatusMutationVariables
  >(ADMIN_SYNC_TOSS_PAYOUT_STATUS, {
    onCompleted: (res) => {
      const r = res.adminSyncTossPayoutStatus;
      if (!r?.ok) {
        notifyError(r?.error ?? "동기화에 실패했습니다.");
        return;
      }
      notify(
        `동기화 완료 · 토스 status: ${r.status ?? "—"}`,
      );
      setTossPayoutId("");
    },
    onError: (e) => notifyError(e.message),
  });

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    const id = tossPayoutId.trim();
    if (!id) {
      notifyError("토스 지급 ID를 입력해주세요. (예: FPA_…)");
      return;
    }
    sync({ variables: { tossPayoutId: id } });
  };

  return (
    <div className="mx-auto max-w-xl px-4 py-8 md:px-6">
      <h1 className="mb-2 text-xl font-semibold text-slate-900">
        토스 지급 상태 동기화
      </h1>
      <p className="mb-6 text-sm text-slate-600">
        <code>payout.changed</code> 웹훅이 오지 않은 경우, 토스 API로 해당 지급
        건의 최신 상태를 조회하여 <code>Payout</code>/<code>Settle</code> 레코드를
        갱신합니다. 토스 가맹점 콘솔과 동일한 지급 ID를 입력합니다.
      </p>

      <form
        className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
        onSubmit={handleSubmit}
      >
        <div>
          <label
            className="mb-1 block text-sm font-medium text-slate-700"
            htmlFor="tossPayoutId"
          >
            토스 지급 ID (FPA_…)
          </label>
          <input
            id="tossPayoutId"
            autoComplete="off"
            spellCheck={false}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm outline-none ring-primary focus:border-primary focus:ring-1"
            placeholder="FPA_..."
            value={tossPayoutId}
            onChange={(e) => setTossPayoutId(e.target.value)}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="rounded bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
        >
          {loading ? "동기화 중…" : "토스와 동기화"}
        </button>
      </form>
    </div>
  );
}
